import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  GatewayIdentity,
  SessionAuth,
  SessionUser,
  SessionUserState,
} from "@/common/gateway-session";
import {
  fetchGatewayIdentityForQuery,
  getGatewaySessionToken,
  hasGatewaySession,
  logoutGatewaySession,
} from "@/common/gateway-session";
import { getQueryClient } from "@/trpc/react";

/**
 * Session seams. Identity comes from /oauth/userinfo via React Query; the
 * bearer comes from the product host's /session/token.
 *
 * THE SESSION MODEL (read it, don't re-derive it per consumer):
 *  - "Signed in" = a valid session token exists. TOKEN PRESENCE is the single
 *    source of truth, INDEPENDENT of whether /oauth/userinfo has resolved.
 *    Read it via useSessionAuthState() (nav, account layout, any "am I signed
 *    in?" gate). NEVER use useUser().isSignedIn as a signed-in gate — that flips
 *    false while identity is merely loading or transiently failing.
 *  - Identity (name/email/avatar/accountId) is a SEPARATE concern that may lag:
 *      signed-in + user-loaded    → useUser().user is set
 *      signed-in + user-loading   → token present, useUser().user still null
 *      signed-in + userinfo-error → token present, useUser().user null (retried)
 *      signed-out                 → no token
 *    Read identity via useUser(); it is null in the middle two states.
 *  - useSessionRefresh() force-refreshes the bearer AND refetches identity, and
 *    WORKS WHEN user is null (unlike user.reload()) — use it for the account
 *    provisioning race (useIsAccountLoaded, "Try again").
 */

export const GATEWAY_IDENTITY_QUERY_KEY = ["gateway-session-identity"] as const;
const GATEWAY_PRESENCE_QUERY_KEY = ["gateway-session-present"] as const;
// userinfo is cheap and rarely changes within a session; refetch lazily.
const GATEWAY_SESSION_STALE_MS = 5 * 60 * 1000;

/** { isSignedIn, isLoaded } — token-presence signed-in state + settle flag. */
export type SessionAuthState = { isSignedIn: boolean; isLoaded: boolean };

/**
 * Prime the identity query cache with a freshly-fetched identity so a route
 * guard's imperative fetchGatewayIdentity() read and the component's useUser()
 * resolve the SAME value. Without this, a pre-onboarding cached identity
 * (cliWelcomeCompleted false) survives its staleTime and makes AccountLayout's
 * effect bounce a just-onboarded user (whom the guard already let in) back to
 * /onboarding. Called from route beforeLoad guards, outside React.
 */
export function primeGatewayIdentity(identity: GatewayIdentity | null): void {
  getQueryClient().setQueryData(GATEWAY_IDENTITY_QUERY_KEY, identity);
}

/**
 * Cache-first identity read for route guards that run on every navigation
 * (unlike fetchGatewayIdentity, which always hits the network). Shares the
 * useUser() query and its staleTime, so a guard read and the rendered identity
 * agree. Returns null when signed out OR when userinfo is unavailable —
 * callers must fail open on null.
 */
export async function ensureGatewayIdentity(): Promise<GatewayIdentity | null> {
  try {
    return await getQueryClient().ensureQueryData({
      queryKey: GATEWAY_IDENTITY_QUERY_KEY,
      queryFn: fetchGatewayIdentityForQuery,
      staleTime: GATEWAY_SESSION_STALE_MS,
    });
  } catch {
    // userinfo unavailable (token present, endpoint degraded) — unresolved.
    return null;
  }
}

/** useUser()-shaped identity sourced from the gateway session. */
export function useGatewaySession(): SessionUserState {
  const query = useQuery({
    queryKey: GATEWAY_IDENTITY_QUERY_KEY,
    // Throws on a transient userinfo failure so React Query retries it, rather
    // than reporting the user signed-out mid-blip.
    queryFn: fetchGatewayIdentityForQuery,
    staleTime: GATEWAY_SESSION_STALE_MS,
  });

  const identity = query.data ?? null;
  const { refetch } = query;

  // Attach reload() and keep the object reference stable per identity so
  // consumers that depend on `user` (Crisp, onboarding) don't churn.
  const user = React.useMemo<SessionUser | null>(() => {
    if (!identity) return null;
    return {
      ...identity,
      reload: async () => {
        // Force a fresh access token first, not just userinfo: consumers rely
        // on reload() to beat the account-provisioning race, so the bearer tRPC
        // sends must actually refresh.
        await getGatewaySessionToken({ skipCache: true });
        await refetch();
      },
    };
  }, [identity, refetch]);

  if (user) return { isLoaded: true, isSignedIn: true, user };
  return {
    isLoaded: query.status !== "pending",
    isSignedIn: false,
    user: null,
  };
}

/**
 * Signed-in state driven by token presence (the authoritative signal), not by
 * userinfo. So while userinfo is still loading or transiently failing every
 * "am I signed in?" consumer stays "signed in" — the nav never flashes sign-in
 * buttons and the account layout never shows "Signing out..." for an authed user.
 */
function useGatewayAuthState(): SessionAuthState {
  const query = useQuery({
    queryKey: GATEWAY_PRESENCE_QUERY_KEY,
    queryFn: hasGatewaySession,
    staleTime: GATEWAY_SESSION_STALE_MS,
  });
  return {
    isSignedIn: query.data === true,
    isLoaded: query.status !== "pending",
  };
}

/**
 * Force-refresh the session: mint a fresh access token (bypassing the cache)
 * and refetch identity + presence. Works even when `user` is null, so the
 * account-provisioning poll recovers before the first userinfo success (unlike
 * user.reload(), which no-ops when there is no user yet).
 */
function useGatewayRefresh(): () => Promise<void> {
  const queryClient = useQueryClient();
  return React.useCallback(async () => {
    await getGatewaySessionToken({ skipCache: true });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: GATEWAY_IDENTITY_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: GATEWAY_PRESENCE_QUERY_KEY }),
    ]);
  }, [queryClient]);
}

export type SessionContextValue = {
  auth: SessionAuth;
  user: SessionUserState;
};

/**
 * The { auth, user } pair pushed into the TanStack router context —
 * auth.isSignedIn is token-based (source of truth), getToken → the gateway
 * access token — alongside the (identity) session for `user`.
 */
export function useSessionContext(): SessionContextValue {
  const session = useGatewaySession();
  const authState = useGatewayAuthState();
  return { auth: buildGatewayAuth(session, authState), user: session };
}

/**
 * Signed-in + loading state for surfaces that gate on "am I signed in?" (CliNav,
 * the account layout's signing-out spinner). Reports real token-presence
 * loading so callers can avoid flashing signed-out while the session resolves.
 */
export function useSessionAuthState(): SessionAuthState {
  return useGatewayAuthState();
}

/**
 * A refresh that works regardless of identity state: force-refreshes the
 * bearer + refetches. Used by the account-provisioning poll + "Try again".
 */
export function useSessionRefresh(): () => Promise<void> {
  return useGatewayRefresh();
}

function buildGatewayAuth(
  session: SessionUserState,
  authState: SessionAuthState,
): SessionAuth {
  return {
    // Token presence is the source of truth for signed-in, not userinfo.
    isSignedIn: authState.isSignedIn,
    userId: session.user?.publicMetadata.accountId ?? null,
    getToken: (options) => getGatewaySessionToken(options),
  };
}

/**
 * Sign-out: revokes the BFF session server-side and follows the Tank sign-out
 * chain. Lives here so callers share one seam.
 */
export function useSessionSignOut(): () => void {
  return React.useCallback(() => {
    void logoutGatewaySession();
  }, []);
}
